import { useEffect, useState } from "react";
import {
  AccountState,
  BridgeStatus,
  ConvergenceMatrix,
  NewsEvent,
  Position,
  RiskState,
  Trade,
  api,
  isError,
} from "../api";

const POLL_MS = 2000;

export interface LiveSnapshot {
  bridge: BridgeStatus | null;
  account: AccountState | null;
  accountError: string | null;
  positions: Position[];
  trades: Trade[];
  risk: RiskState | null;
  matrix: ConvergenceMatrix | null;
  news: NewsEvent[];
  lastTick: Date | null;
}

const EMPTY: LiveSnapshot = {
  bridge: null,
  account: null,
  accountError: null,
  positions: [],
  trades: [],
  risk: null,
  matrix: null,
  news: [],
  lastTick: null,
};

/** Single batched poller for the big-screen live dashboard. Every tick
 * fires the seven endpoints in parallel and updates the whole snapshot
 * at once so the screen doesn't flicker as fields arrive piecemeal. */
export function useLivePoll(): LiveSnapshot {
  const [snap, setSnap] = useState<LiveSnapshot>(EMPTY);

  useEffect(() => {
    let cancelled = false;

    async function tick() {
      const [b, a, p, t, r, m, n] = await Promise.all([
        api.bridgeStatus(),
        api.accountState(),
        api.accountPositions(),
        api.trades(15),
        api.riskState(),
        api.convergenceMatrix(),
        api.newsEvents(12),
      ]);
      if (cancelled) return;
      setSnap({
        bridge: isError(b) ? { connected: false } : b,
        account: isError(a) ? null : a,
        accountError: isError(a) ? a.error : null,
        positions: isError(p) ? [] : p.positions,
        trades: isError(t) ? [] : t.trades,
        risk: isError(r) ? null : r,
        matrix: isError(m) ? null : m,
        news: isError(n) ? [] : n.events,
        lastTick: new Date(),
      });
    }

    void tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return snap;
}
